import { useCallback, useContext, useMemo, useReducer, createContext, ReactNode } from 'react';
import { Interval, ViewMode } from 'app/agriculture/AgTimeline/types';
import { viewModeConfig, ViewModeConfig } from 'app/agriculture/AgTimeline/viewModeConfig';
import { useThrottle } from 'app/agriculture/hooks/useThrottle/useThrottle';
import { getCursorTimeForNewInterval } from 'app/agriculture/AgTimeline/utils/getCursorTimeForNewInterval';
import { useThrottledNow } from 'app/agriculture/AgTimeline/hooks/useThrottledNow';
import { clamp as clampDate, isAfter } from 'date-fns';
import { TZDate } from '@date-fns/tz';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';

const TimelineExpandedStateContext = createContext(false);
const TimelineCursorTimeContext = createContext<Date | undefined | null>(null);
const TimelineThrottledCursorTimeContext = createContext<Date | undefined | null>(null);
const TimelineIntervalContext = createContext<Interval<Date> | null>(null);
const TimelinePixelWidthContext = createContext<number | null>(null);
const TimelineViewModeContext = createContext<
    (ViewModeConfig & { id: keyof typeof ViewMode }) | null
>(null);
export const TimelineDispatchContext = createContext<{
    setCursorTime: (cursorTime?: Date) => void;
    setInterval: (interval: Interval<Date>, options?: { cursorTime?: Date | null }) => void;
    setViewMode: (viewMode: keyof typeof ViewMode) => void;
    setPixelWidth: (pixelWidth: number) => void;
    expandTimeline: () => void;
    collapseTimeline: () => void;
} | null>(null);

interface TimelineContextState {
    /** Is the timeline open? */
    isTimelineExpanded: boolean;

    /** The current time as shown by the scrubber */
    cursorTime?: Date;
    /**
     * The start point of the visible interval on the timeline
     * The time represented by the left-most point of the timeline
     */
    intervalStart: Date;
    /**
     * The end point of the visible interval on the timeline
     * The time represented by the right-most point of the timeline
     */
    intervalEnd: Date;
    /**
     * The width of the timeline interval in pixels, from start to end
     */
    pixelWidth: number;
    /**
     * This controls how the timeline is configured in terms of how large an interval
     * see ../viewModeConfig.ts for more
     */
    viewMode: keyof typeof ViewMode;
}

type TimelineDispatchAction =
    | { type: 'setCursorTime'; cursorTime?: Date }
    | { type: 'setInterval'; interval: Interval<Date>; cursorTime?: Date | null }
    | { type: 'setPixelWidth'; pixelWidth: number }
    | { type: 'setViewMode'; viewMode: keyof typeof ViewMode }
    | { type: 'expand' }
    | { type: 'collapse' };

export function TimelineContextProvider({
    children,
    initialTimelineState = {
        isTimelineExpanded: false,
        cursorTime: undefined,
        pixelWidth: 0,
        viewMode: 'Week',
    },
}: {
    children: ReactNode;
    initialTimelineState?: Omit<TimelineContextState, 'intervalStart' | 'intervalEnd'>;
}) {
    const { timeZone } = useTimeZone();
    const now = new TZDate(new Date(), timeZone);
    const initialInterval = viewModeConfig[initialTimelineState.viewMode].getNewVisibleInterval(
        initialTimelineState.cursorTime ?? now,
    );

    const [
        { isTimelineExpanded, cursorTime, intervalStart, intervalEnd, pixelWidth, viewMode },
        dispatch,
    ] = useReducer(
        function timelineContextReducer(
            state: TimelineContextState,
            action: TimelineDispatchAction,
        ): TimelineContextState {
            switch (action.type) {
                case 'setPixelWidth': {
                    return {
                        ...state,
                        pixelWidth: action.pixelWidth,
                    };
                }
                case 'setCursorTime': {
                    return {
                        ...state,
                        cursorTime: action.cursorTime,
                    };
                }
                case 'setInterval': {
                    // when the interval is updated we need to make sure the cursor time is within the new interval
                    const newCursorTime =
                        action.cursorTime === null
                            ? undefined
                            : (action.cursorTime ??
                              getCursorTimeForNewInterval(action.interval, now, state.cursorTime));

                    return {
                        ...state,
                        intervalStart: action.interval.start,
                        intervalEnd: action.interval.end,
                        cursorTime: newCursorTime,
                    };
                }
                case 'setViewMode': {
                    // when the view mode changes we want to update the interval to match the new duration of the view mode
                    const { getNewVisibleInterval } = viewModeConfig[action.viewMode];
                    const newInterval = getNewVisibleInterval(state.cursorTime ?? now);
                    // then make sure the cursor time is within the new interval
                    const newCursorTime = getCursorTimeForNewInterval(
                        newInterval,
                        now,
                        state.cursorTime,
                    );

                    return {
                        ...state,
                        cursorTime: newCursorTime,
                        intervalStart: newInterval.start,
                        intervalEnd: newInterval.end,
                        viewMode: action.viewMode,
                    };
                }
                case 'expand': {
                    return {
                        ...state,
                        isTimelineExpanded: true,
                    };
                }
                case 'collapse': {
                    return {
                        ...state,
                        isTimelineExpanded: false,
                    };
                }
                default: {
                    return state;
                }
            }
        },
        {
            ...initialTimelineState,
            intervalStart: initialInterval.start,
            intervalEnd: initialInterval.end,
        },
    );

    const memoisedTimelineExpandedState = useMemo(() => isTimelineExpanded, [isTimelineExpanded]);
    const throttledCursorTime = useThrottle(cursorTime, 100);
    const throttledNow = useThrottledNow();
    const memoisedThrottledCursorTime = useMemo(
        () =>
            throttledCursorTime == null
                ? throttledCursorTime
                : new TZDate(throttledCursorTime, timeZone),
        [throttledCursorTime, timeZone],
    );
    const memoisedCursorTime = useMemo(
        () => (cursorTime == null ? cursorTime : new TZDate(cursorTime, timeZone)),
        [cursorTime, timeZone],
    );
    const memoisedInterval = useMemo(
        () => ({
            start: new TZDate(intervalStart, timeZone),
            end: new TZDate(intervalEnd, timeZone),
        }),
        [intervalStart, intervalEnd, timeZone],
    );
    const memoisedPixelWidth = useMemo(() => pixelWidth, [pixelWidth]);
    const memoisedViewMode = useMemo(
        () => ({ ...viewModeConfig[viewMode], id: viewMode }),
        [viewMode],
    );

    const setCursorTime = useCallback(
        (cursorTime?: Date) => {
            const newCursorTime =
                !!cursorTime && isAfter(cursorTime, throttledNow) ? throttledNow : cursorTime;
            dispatch({
                type: 'setCursorTime',
                cursorTime:
                    newCursorTime == null ? newCursorTime : new TZDate(newCursorTime, timeZone),
            });
        },
        [throttledNow, timeZone],
    );

    const setInterval = useCallback(
        ({ start, end }: Interval<Date>, { cursorTime }: { cursorTime?: Date | null } = {}) => {
            const newCursorTime =
                cursorTime == null
                    ? cursorTime
                    : clampDate(new TZDate(cursorTime, timeZone), { start: 0, end: throttledNow });
            dispatch({
                type: 'setInterval',
                interval: {
                    start: new TZDate(start, timeZone),
                    end: new TZDate(end, timeZone),
                },
                cursorTime: newCursorTime,
            });
        },
        [throttledNow, timeZone],
    );

    const setViewMode = useCallback((viewMode: keyof typeof ViewMode) => {
        dispatch({ type: 'setViewMode', viewMode });
    }, []);

    const setPixelWidth = useCallback((pixelWidth: number) => {
        dispatch({ type: 'setPixelWidth', pixelWidth });
    }, []);

    const expandTimeline = useCallback(() => {
        dispatch({ type: 'expand' });
    }, []);

    const collapseTimeline = useCallback(() => {
        dispatch({ type: 'collapse' });
    }, []);

    return (
        <TimelineExpandedStateContext.Provider value={memoisedTimelineExpandedState}>
            <TimelineCursorTimeContext.Provider value={memoisedCursorTime}>
                <TimelineThrottledCursorTimeContext.Provider value={memoisedThrottledCursorTime}>
                    <TimelineIntervalContext.Provider value={memoisedInterval}>
                        <TimelinePixelWidthContext.Provider value={memoisedPixelWidth}>
                            <TimelineViewModeContext.Provider value={memoisedViewMode}>
                                <TimelineDispatchContext.Provider
                                    value={{
                                        setCursorTime,
                                        setInterval,
                                        setViewMode,
                                        setPixelWidth,
                                        expandTimeline,
                                        collapseTimeline,
                                    }}
                                >
                                    {children}
                                </TimelineDispatchContext.Provider>
                            </TimelineViewModeContext.Provider>
                        </TimelinePixelWidthContext.Provider>
                    </TimelineIntervalContext.Provider>
                </TimelineThrottledCursorTimeContext.Provider>
            </TimelineCursorTimeContext.Provider>
        </TimelineExpandedStateContext.Provider>
    );
}

export function useTimelineExpandedState() {
    return useContext(TimelineExpandedStateContext);
}

export function useTimelineCursorTime() {
    const context = useContext(TimelineCursorTimeContext);
    if (context === null) throw new Error('TimelineCursorTimeContext does not exist');

    return context;
}

export function useThrottledTimelineCursorTime({
    throttleDelay = 500,
}: { throttleDelay?: number } = {}) {
    const context = useContext(TimelineCursorTimeContext);
    if (context === null) throw new Error('TimelineCursorTimeContext does not exist');

    return useThrottle(context, throttleDelay);
}

export function useTimelineInterval() {
    const context = useContext(TimelineIntervalContext);
    if (context === null) throw new Error('TimelineIntervalContext does not exist');

    return context;
}

export function useTimelinePixelWidth() {
    const context = useContext(TimelinePixelWidthContext);
    if (context === null) throw new Error('TimelinePixelWidthContext does not exist');

    return context;
}

export function useTimelineViewMode() {
    const context = useContext(TimelineViewModeContext);
    if (context === null) throw new Error('TimelineViewModeContext does not exist');

    return context;
}

export function useTimelineCondensePellets() {
    const context = useContext(TimelineViewModeContext);
    if (context === null) throw new Error('TimelineViewModeContext does not exist');

    return context.condensePellets;
}

export function useTimelineDispatch() {
    const context = useContext(TimelineDispatchContext);
    if (context === null) throw new Error('TimelineDispatchContext does not exist');

    return context;
}
